import { binds } from "../binds";
import { useAtomValues } from "../react";
import { type Atom, getDefaultStore } from "jotai";
import { type Observable, fromEventPattern, startWith } from "rxjs";

type Store = ReturnType<typeof getDefaultStore>;

export class BaseStore {
  static defaultStore: Store = getDefaultStore();

  store: Store;

  constructor(store?: Store) {
    this.store = store ?? BaseStore.defaultStore;
  }

  get: Store["get"] = (...args) => {
    return this.store.get(...args);
  };

  set: Store["set"] = (...args) => {
    return this.store.set(...args);
  };

  sub: Store["sub"] = (...args) => {
    return this.store.sub(...args);
  };

  bind = binds(this as Omit<this, keyof BaseStore>);

  use = useAtomValues(this as Omit<this, keyof BaseStore>);

  fromAtom<A>(
    atom: Atom<A>,
    {
      skipFirst,
    }: {
      /**
       * Skip the first value from the atom. Default is `false`.
       */
      skipFirst?: boolean;
    } = {}
  ): Observable<A> {
    const updaste$: Observable<A> = fromEventPattern(
      (handler) => {
        return this.sub(atom, () => handler(this.get(atom)));
      },
      (handler, unsubscribe) => {
        unsubscribe();
      }
    );

    if (skipFirst) {
      return updaste$;
    }
    const startValue = this.get(atom);
    return updaste$.pipe(startWith(startValue));
  }
}
